import React from "react";
import { Popper } from "@mui/material";
import { Trans } from "react-i18next";
import ButtonAction from "../../../../components/buttons/ButtonAction";
import { CloseModalIcon, IconDot, IconPlay } from "../../../../components/buttons/IconButtons";
import WalkthroughApp from "../Functionality/WalkthroughApp";
import { WALKTHROUGH_POPPER_MODIFIERS } from "../Model/Model";

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Walkthrough of Event Monitoring. The welcome card is centered and the following
 * steps point to the action they explain; the steps with a modal are shown over the modal they describe.
 *
 * @param {number} step - 0 for the welcome card, 1..n for the steps.
 * @param {Array} steps - Steps the permissions of the admin let it follow.
 * @param {Function} onNext - Goes to the next step, closes after the last one.
 * @param {Function} onClose - Skips the tutorial.
 * @param {Function} t - Translation function.
 */
const Walkthrough = ({ step, steps, onNext, onClose, t }) => {
    const {
        anchorEl,
        popperRef,
        resolved,
        currentStep,
        totalSteps,
        isIntro,
        isLastStep,
        progressClassName
    } = WalkthroughApp({ step, steps });

    if (!resolved) return null;

    const card = (
        <div className={`event-monitoring-walkthrough w-400px border-radius-4px d-flex flex-column gap-4 ${anchorEl ? '' : 'event-monitoring-walkthrough-centered'}`}>
            {anchorEl && <span data-popper-arrow className='event-monitoring-walkthrough-arrow' />}
            <div className='d-flex flex-column gap-12px'>
                <div className='d-flex justify-content-between align-items-center'>
                    <div className='d-flex align-items-center gap-2 fs-16px font-color-shades-50'>
                        <p className='ibl-font-w-600'>{t('event_monitoring')}</p>
                        {!isIntro &&
                            <>
                                <IconDot className='w-4px h-4px icon-color-shades-50' />
                                <p className='ibl-font-w-400'>{t('step_x_of_y', { step, total: totalSteps })}</p>
                            </>
                        }
                    </div>
                    <div className='btn-icon-cancel cursor-pointer'>
                        <CloseModalIcon id='btn_w_close' onClick={onClose} />
                    </div>
                </div>
                {!isIntro &&
                    <div className='event-monitoring-walkthrough-progress h-8px border-radius-8px'>
                        <div className={progressClassName} />
                    </div>
                }
            </div>

            <div className='d-flex flex-column gap-12px'>
                {currentStep.BANNER &&
                    <img src={currentStep.BANNER} alt={t(currentStep.TITLE)} className='event-monitoring-walkthrough-banner d-block w-100 h-200px border-radius-4px' />
                }
                <div className='d-flex flex-column gap-2 font-color-general-00'>
                    <p className='fs-16px ibl-font-w-500'>{t(currentStep.TITLE)}</p>
                    <p className='fs-12px ibl-font-w-300'>{t(currentStep.DESCRIPTION)}</p>
                    {(currentStep.ACTIONS ?? []).map((action) => (
                        <div key={action.KEY} className='d-flex align-items-center gap-2 pl-2'>
                            <action.Icon className='flex-shrink-0 w-13px h-13px icon-color-general-00' />
                            <p className='fs-12px ibl-font-w-300'>
                                <Trans i18nKey={action.TRANS} components={{ bold: <strong className='ibl-font-w-600' /> }} />
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {isIntro
                ? <ButtonAction
                    id='btn_w_start'
                    name='start_tutorial'
                    className='btn-secondary-600 w-100'
                    Icon={IconPlay}
                    splitName={false}
                    onClick={onNext}
                />
                : <div className='d-flex gap-3'>
                    <ButtonAction
                        id='btn_w_skip'
                        name='skip'
                        className='event-monitoring-walkthrough-skip w-50'
                        splitName={false}
                        onClick={onClose}
                    />
                    <ButtonAction
                        id='btn_w_next'
                        name={isLastStep ? 'finish' : 'next'}
                        className='btn-secondary-600 w-50'
                        splitName={false}
                        onClick={onNext}
                    />
                </div>
            }
        </div>
    );

    // The welcome card is shown over the shadow of the page loader //
    if (!anchorEl) {
        return (
            <>
                {isIntro && <div className='event-monitoring-walkthrough-backdrop background-general-950-opacity05' />}
                {card}
            </>
        );
    }

    return (
        <Popper
            open={true}
            anchorEl={anchorEl}
            popperRef={popperRef}
            placement={currentStep.PLACEMENT}
            modifiers={WALKTHROUGH_POPPER_MODIFIERS}
            className='event-monitoring-walkthrough-popper'
        >
            {card}
        </Popper>
    );
};

export default Walkthrough;
